<template>
  <div class="main-container-shipmentinfo">
    <div class="inner-container">
        <div class="confirm-container-main">
            <div class="close-shipment-info-container">
                <svg class="close-shipment-info" @click="closeTripCostEditor()" viewBox="0 0 512 512" xml:space="preserve" xmlns="http://www.w3.org/2000/svg">
                    <path d="M255.997 460.351c112.685 0 204.355-91.668 204.355-204.348S368.682 51.648 255.997 51.648c-112.68 0-204.348 91.676-204.348 204.355s91.668 204.348 204.348 204.348zm0-376.463c94.906 0 172.123 77.209 172.123 172.115 0 94.898-77.217 172.117-172.123 172.117-94.9 0-172.108-77.219-172.108-172.117-.001-94.906 77.207-172.115 172.108-172.115z" fill="#32ccfe" class="fill-000000"></path>
                    <path d="M172.077 341.508a18.35 18.35 0 0 0 12.903 5.27c4.776 0 9.54-1.84 13.151-5.512l57.865-58.973 57.878 58.973a18.372 18.372 0 0 0 13.146 5.512c4.658 0 9.316-1.746 12.902-5.27 7.264-7.125 7.369-18.793.242-26.051l-58.357-59.453 58.357-59.461c7.127-7.258 7.021-18.92-.242-26.047-7.252-7.123-18.914-7.018-26.049.24l-57.878 58.971-57.865-58.971c-7.135-7.264-18.797-7.363-26.055-.24-7.258 7.127-7.369 18.789-.236 26.047l58.351 59.461-58.351 59.453c-7.131 7.258-7.02 18.926.238 26.051z" fill="#32ccfe" class="fill-000000"></path>
                </svg>
            </div>

            <div class="confirm-header-container">
                <h1 class="package-details-header">Trip Cost</h1>
                <span class="trip-id-badge">{{ tripSheetId }}</span>
                <span v-if="tripData" class="tce-status-badge">{{ tripData.costStatus || 'open' }}</span>
            </div>

            <div class="inner-overflow-container">
                <!-- Trip identity + default quantities (Requirement 2.5). -->
                <section class="tce-section">
                    <div class="section-header">
                        <h2>Trip</h2>
                    </div>
                    <div class="section-body">
                        <p v-if="loading" class="tce-placeholder">Loading trip...</p>
                        <div v-else-if="tripData">
                            <div class="tce-identity-grid">
                                <div class="tce-field">
                                    <span class="tce-label">Trip Sheet</span>
                                    <span class="tce-value">{{ tripData.TripSheetId }}</span>
                                </div>
                                <div class="tce-field">
                                    <span class="tce-label">Route</span>
                                    <span class="tce-value">{{ display(tripData.routeCode) }}</span>
                                </div>
                                <div class="tce-field">
                                    <span class="tce-label">Driver</span>
                                    <span class="tce-value">{{ display(tripData.driverCode) }}</span>
                                </div>
                                <div class="tce-field">
                                    <span class="tce-label">Vendor</span>
                                    <span class="tce-value">{{ display(vendorName || tripData.vendorName || tripData.vendor) }}</span>
                                </div>
                                <div class="tce-field">
                                    <span class="tce-label">Dispatch Date</span>
                                    <span class="tce-value">{{ display(tripData.TripDate) }}</span>
                                </div>
                                <div class="tce-field">
                                    <span class="tce-label">Close Date</span>
                                    <span class="tce-value">{{ display(tripData.SignOffDate || tripData.closedOn) }}</span>
                                </div>
                            </div>

                            <!-- Default quantities from the TripWaypoint "*" summary record.
                                 Display-only, not stored (Requirement 2.5). -->
                            <div class="tce-quantities">
                                <span class="tce-subheading">Trip Quantities (from waypoint summary)</span>
                                <div class="tce-identity-grid">
                                    <div class="tce-field">
                                        <span class="tce-label">Stop Count</span>
                                        <span class="tce-value">{{ display(tripQuantities.orderCount) }}</span>
                                    </div>
                                    <div class="tce-field">
                                        <span class="tce-label">Total Distance</span>
                                        <span class="tce-value">{{ formattedTotalDistance }}</span>
                                    </div>
                                    <div class="tce-field">
                                        <span class="tce-label">Total Time</span>
                                        <span class="tce-value">{{ formattedTotalTime }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p v-else class="tce-placeholder">Trip not found.</p>
                    </div>
                </section>

                <!-- Cost lines sourced from the TRIP Product table + Vendor fuel surcharge
                     (Requirement 2.6). Quantities are pre-populated display-only from the trip
                     summary. Line math/totals (Task 17) and the approval mutation (Task 18) are
                     implemented in later tasks. -->
                <section class="tce-section">
                    <div class="section-header">
                        <h2>Cost Lines</h2>
                    </div>
                    <div class="section-body">
                        <p v-if="loading" class="tce-placeholder">Loading vendor products...</p>

                        <template v-else>
                            <!-- Payment-model product selector (Requirement 7.1). TRIP products
                                 are grouped by productCode into options labeled by the base row's
                                 productName. Selecting an option builds one editable cost line per
                                 service via onSelectProduct (Requirements 7.2–7.5). -->
                            <div v-if="tripProductOptions.length" class="tce-product-select">
                                <label class="tce-label" for="tce-product-select">Payment model</label>
                                <select
                                    id="tce-product-select"
                                    v-model="selectedProductCode"
                                    :disabled="isApproved"
                                    @change="onSelectProduct(selectedProductCode)"
                                >
                                    <option value="">Select a trip-cost product…</option>
                                    <option
                                        v-for="option in tripProductOptions"
                                        :key="option.productCode"
                                        :value="option.productCode"
                                    >
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>

                            <!-- Editable cost lines for the selected product. Quantity is an
                                 operator-editable number input bound to line.quantity so lines can
                                 be adjusted before approval (Requirement 7.5); it is disabled once
                                 the trip cost is approved (read-only). -->
                            <div v-if="costLines.length" class="tce-table-wrapper">
                                <table class="tce-table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Service</th>
                                            <th>Description</th>
                                            <th class="tce-num">Quantity</th>
                                            <th class="tce-num">Price</th>
                                            <th class="tce-num">Amount</th>
                                            <th>FS Code</th>
                                            <th class="tce-num">FS Rate</th>
                                            <th class="tce-num">Fuel Surcharge</th>
                                            <th class="tce-num">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <!-- Live preview per line. Amount/FS/Total are computed
                                             with the same pure logic used at approval
                                             (computeLine), so the displayed numbers always match
                                             what will be persisted. Slab lines show qty 1. -->
                                        <tr
                                            v-for="(line, index) in previewLines"
                                            :key="index"
                                            :class="{ 'tce-row-excluded': !line._kept }"
                                        >
                                            <td>{{ line.productCode }}</td>
                                            <td>{{ line.serviceCode }}</td>
                                            <td>{{ display(line.description) }}</td>
                                            <td class="tce-num">
                                                <input
                                                    type="number"
                                                    class="tce-qty-input"
                                                    v-model="costLines[index].quantity"
                                                    :disabled="isApproved"
                                                    min="0"
                                                    step="any"
                                                />
                                            </td>
                                            <td class="tce-num">{{ money(line.price) }}</td>
                                            <td class="tce-num">{{ money(line.amount) }}</td>
                                            <td>{{ display(line.fuelSurchargeCode) }}</td>
                                            <td class="tce-num">{{ display(line.fuelSurchargeRate) }}</td>
                                            <td class="tce-num">{{ money(line.fuelSurcharge) }}</td>
                                            <td class="tce-num">{{ money(line.total) }}</td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr class="tce-totals-row">
                                            <td colspan="8" class="tce-num tce-totals-label">
                                                Fuel Surcharge
                                            </td>
                                            <td class="tce-num">{{ money(costPreview.fuelSurcharge) }}</td>
                                            <td></td>
                                        </tr>
                                        <tr class="tce-totals-row tce-grand-total">
                                            <td colspan="9" class="tce-num tce-totals-label">
                                                Trip Total
                                            </td>
                                            <td class="tce-num">{{ money(costPreview.tripTotal) }}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                                <p class="tce-preview-note">
                                    Amounts update as you edit quantities. Lines with a blank or zero
                                    quantity are excluded from the approved total.
                                </p>
                            </div>

                            <p v-if="!tripProductOptions.length" class="tce-placeholder">
                                No trip-cost (serviceNetwork "TRIP") products found for this trip.
                            </p>
                            <p
                                v-else-if="selectedProductCode && !costLines.length"
                                class="tce-placeholder"
                            >
                                This product has no services to render.
                            </p>
                        </template>
                    </div>
                </section>

                <!-- Status / approval actions + costStatus transition rules (Task 19).
                     Rules enforced here (Requirement 3):
                       3.1 "approved" is irreversible — record becomes read-only immediately.
                       3.2 "ignored"/"abandoned" are reversible — back to "open" or to each other.
                       3.3 "ignored"/"abandoned" may transition directly to "approved".
                       3.4 Only users in the BillingAdmin Cognito group may set
                           "ignored"/"abandoned". -->
                <section class="tce-section">
                    <div class="section-header">
                        <h2>Status</h2>
                    </div>
                    <div class="section-body">
                        <!-- No-vendor notice. The vendor is attached when a driver with a vendor
                             is assigned to the trip. Without it the tripInvoice Lambda is
                             vendor-scoped and will skip the trip, so approval is blocked until a
                             vendor is present. Ignore/Abandon remain available so ops can still
                             clear the trip out. -->
                        <p
                            v-if="tripData && !isApproved && !hasVendor"
                            class="tce-placeholder tce-no-vendor-note"
                        >
                            No vendor assigned to this trip. A vendor is set when a driver with a
                            vendor is assigned. Approval is disabled until a vendor is present; the
                            invoice process is vendor-scoped and would skip this trip.
                        </p>

                        <!-- Current status. Once approved the cost node is permanent and the
                             record is read-only (Requirement 2.7 / 3.1). -->
                        <div class="tce-status-row">
                            <span class="tce-label">Current status</span>
                            <span class="tce-value tce-status-current">{{ currentStatus }}</span>
                        </div>

                        <!-- Approved: locked / irreversible (Requirement 3.1). No controls are
                             rendered — the record is read-only. -->
                        <p v-if="isApproved" class="tce-placeholder tce-locked-note">
                            This trip cost was approved on {{ display(tripData && tripData.costApprovedOn) }}
                            and is locked. Approved trips are irreversible and cannot be edited.
                        </p>

                        <!-- The entire cost function (approve + set-status + line editing) is
                             restricted to the BillingAdmin group (Requirement 3.5 / Task 20).
                             Non-BillingAdmin users see the cost read-only with no controls. -->
                        <template v-else-if="tripData && canEditCost">
                            <p class="tce-placeholder">
                                Approve to write the cost node atomically with costStatus "approved",
                                costApprovedOn, and vendorName. Approval is irreversible.
                            </p>

                            <!-- Set-status controls. "ignored"/"abandoned" require the
                                 BillingAdmin group (Requirement 3.4); reverting to "open" is a
                                 reversal of those states and is likewise gated. -->
                            <div v-if="canSetOpsStatus" class="tce-status-controls">
                                <span class="tce-subheading">Set status</span>
                                <div class="tce-status-buttons">
                                    <button
                                        v-if="currentStatus !== 'open'"
                                        class="tce-btn"
                                        :disabled="settingStatus || approving || loading"
                                        @click="setStatus('open')"
                                    >
                                        Reopen (open)
                                    </button>
                                    <button
                                        v-if="currentStatus !== 'ignored'"
                                        class="tce-btn"
                                        :disabled="settingStatus || approving || loading"
                                        @click="setStatus('ignored')"
                                    >
                                        Ignore
                                    </button>
                                    <button
                                        v-if="currentStatus !== 'abandoned'"
                                        class="tce-btn"
                                        :disabled="settingStatus || approving || loading"
                                        @click="setStatus('abandoned')"
                                    >
                                        Abandon
                                    </button>
                                </div>
                            </div>
                            <p v-else class="tce-placeholder tce-gate-note">
                                Setting "ignored" or "abandoned" requires the BillingAdmin group.
                            </p>
                        </template>

                        <!-- Non-BillingAdmin users: cost is view-only, no approve/edit controls
                             (Requirement 3.5 / Task 20). -->
                        <p v-else-if="tripData && !canEditCost" class="tce-placeholder tce-gate-note">
                            Approving and editing trip cost requires the BillingAdmin group. This
                            trip cost is view-only.
                        </p>

                        <div class="tce-actions">
                            <button class="tce-btn tce-btn-cancel" @click="closeTripCostEditor()">Close</button>
                            <!-- Approve is allowed from open/ignored/abandoned (Requirement 3.3).
                                 Hidden once approved (Requirement 3.1) and gated to the BillingAdmin
                                 group (Requirement 3.5 / Task 20). -->
                            <!-- Approve requires a vendor (hasVendor): the tripInvoice Lambda is
                                 vendor-scoped and would skip a vendor-less approved trip, so the
                                 button is disabled until a vendor is assigned. -->
                            <button
                                v-if="!isApproved && tripData && canEditCost"
                                class="tce-btn tce-btn-approve"
                                :disabled="approving || settingStatus || loading || !hasVendor"
                                :title="!hasVendor ? 'Assign a driver with a vendor before approving' : ''"
                                @click="approveTripCost()"
                            >
                                {{ approving ? "Approving..." : "Approve" }}
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
  </div>
  <alert-user v-if="toggleAlertBox" @closeAlertBox="closeAlertBox($event)" :message="alertMessage"/>
</template>

<script>
// TripCostEditor — per-trip cost editor for the Trip Cost Approval dashboard (Task 16).
//
// Scope of this task:
//   - Show trip identity (TripSheetId, routeCode, driverCode, vendor, dispatch/close dates).
//   - Pre-populate default quantities from the TripWaypoint "*" summary record (orderCount,
//     totalDistance, totalTime) — display-only, not stored (Requirement 2.5).
//   - Source cost-line prices from the Product table where serviceNetwork == "TRIP"
//     (matched by productCode + serviceCode), plus fuel surcharge rates and vendorName from
//     the Vendor record (Requirement 1.1).
//
// Also implemented here: the atomic approval mutation (Task 18) and the costStatus
// transition-rule enforcement (Task 19) — "approved" is irreversible/read-only,
// "ignored"/"abandoned" are reversible and can go directly to "approved", and only BillingAdmin
// users may set "ignored"/"abandoned".
//
// Also implemented here: component-level group gating (Task 20) — approve, set-status, and line
// editing are restricted to the BillingAdmin Cognito group (Admin retains access) via v-if on the
// controls, backed by a guard in approveTripCost/setStatus. The /TripCostDashboard route and the
// TripCostDashboard host are likewise BillingAdmin-gated.
//
// Out of scope here (later tasks): line math + totals (Task 17).
//
// All queries are inline and omit createdAt/updatedAt to avoid AWSDateTime null issues
// (established codebase pattern).

import { Auth, API } from "aws-amplify";
import AlertUser from "../../../components/Popups/AlertUser.vue";
import { autoQuantityFor, buildCostNode, computeLine } from "./tripCostLogic.js";

export default {
    name: "TripCostEditor",

    components: {
        AlertUser,
    },

    props: {
        tripSheetId: { type: [String, Number], default: null },
        jwtToken: { type: String, default: null },
    },

    emits: ["closeTripCostEditor"],

    data() {
        return {
            tripData: null,           // loaded TripSheet identity + costStatus
            tripQuantities: {         // display-only defaults from TripWaypoint "*"
                orderCount: null,
                totalDistance: null,
                totalTime: null,
            },
            vendorName: null,         // denormalized from the Vendor record
            fuelSurchargeList: [],    // Vendor.fuelSurcharge rates (parsed AWSJSON list)
            tripProducts: [],         // Product table rows where serviceNetwork == "TRIP"
            selectedProductCode: "",  // productCode of the payment-model option chosen in the dropdown
            costLines: [],            // working cost lines (price/FS sourced; qty pre-populated)
            // SAMEDAY per-leg road distances from the TripWaypoint sequence 1/2 records,
            // resolved on demand via the tripWaypointByStop GSI when the selected product has a
            // PICKUPMILES or DELIVERYMILES service. null until fetched; null => blank quantity.
            pickupLegDistance: null,  // sequence=1 fromPreviousDistance (depot->pickup)
            deliveryLegDistance: null,// sequence=2 fromPreviousDistance (pickup->delivery)
            loadingLegDistances: false,
            loading: false,
            approving: false,         // guards the approval mutation from double-submit
            settingStatus: false,     // guards the set-status mutation from double-submit
            // Cognito group flags for the current user. Only BillingAdmin (or Admin) may set
            // "ignored"/"abandoned" (Requirement 3.4). Read on mount, mirrors the group-reading
            // pattern used by the dashboards (VendorDashboard, AdminBillingDashboard).
            AdminUser: false,
            BillingAdmin: false,
            toggleAlertBox: false,
            alertMessage: "",
        };
    },

    computed: {
        // Approved trips are locked and read-only (Requirement 2.7 / 3.1).
        isApproved() {
            return this.tripData?.costStatus === "approved";
        },

        // Normalized current status; defaults to "open" when unset.
        currentStatus() {
            return this.tripData?.costStatus || "open";
        },

        // Whether the trip has a vendor assigned. The vendor is attached when a driver with a
        // vendor is assigned to the trip. It is required to approve: the tripInvoice Lambda is
        // vendor-scoped (it resolves products and fuel-surcharge rates from the trip's Vendor
        // record), so approving a vendor-less trip produces a locked, un-invoiceable trip that
        // the Lambda silently skips. Approval is blocked until a vendor is present.
        hasVendor() {
            return !!this.tripData?.vendor;
        },

        // Only users in the BillingAdmin group (Admin retains access) may set
        // "ignored"/"abandoned" or reverse them back to "open" (Requirement 3.4).
        canSetOpsStatus() {
            return this.BillingAdmin || this.AdminUser;
        },

        // The entire cost function — approve and per-line editing — is restricted to the
        // BillingAdmin Cognito group (Admin retains access) (Requirement 3.5 / Task 20). Used to
        // gate the approve action and line-editing controls at the component level.
        canEditCost() {
            return this.BillingAdmin || this.AdminUser;
        },

        // Total distance (miles) rounded to two decimals for display. Falls back to the empty
        // dash when unset/non-numeric.
        formattedTotalDistance() {
            const raw = this.tripQuantities.totalDistance;
            if (raw === null || raw === undefined || raw === "") return "—";
            const n = Number(raw);
            if (!Number.isFinite(n)) return "—";
            return n.toFixed(2);
        },

        // Total time is stored in minutes (matches TripWaypointTable / OptimizedManifestMap).
        // Display it as hours and minutes, e.g. "2h 35m" (or "45m" / "3h").
        formattedTotalTime() {
            const raw = this.tripQuantities.totalTime;
            if (raw === null || raw === undefined || raw === "") return "—";
            const rawMinutes = Number(raw);
            if (!Number.isFinite(rawMinutes)) return "—";
            const totalMinutes = Math.round(rawMinutes);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            if (hours && minutes) return `${hours}h ${minutes}m`;
            if (hours) return `${hours}h`;
            return `${minutes}m`;
        },

        // Group the loaded TRIP products by productCode into selectable payment-model options
        // for the Cost Lines dropdown (Requirement 7.1). A payment-model "product" is a base row
        // (additionalService !== true) plus N additional-service rows sharing the same
        // productCode. Mirrors the group-by-productCode / getGroupName convention in
        // ProductTable.vue / ProductManagementTable.vue:
        //   - the base row is the one with additionalService !== true (lowest productSequence
        //     when several exist);
        //   - the option label is the base row's productName, falling back to productCode when
        //     the name is missing (or when no base row exists for the group).
        // Groups are returned in first-seen order so the dropdown ordering is stable.
        tripProductOptions() {
            const tripVendor = this.tripData?.vendor || null;

            // Vendor scoping is additive: a trip sees every shared "*" product PLUS every product
            // for its own vendor. Other vendors' products are excluded. Shared "*" products appear
            // on all trips (e.g. a charge that applies to every trip); vendor products appear only
            // for that vendor.
            const eligible = this.tripProducts.filter(
                (p) => p.vendor === "*" || (tripVendor && p.vendor === tripVendor)
            );

            const byCode = new Map();
            for (const product of eligible) {
                const code = product.productCode;
                if (!code) continue;
                if (!byCode.has(code)) byCode.set(code, []);
                byCode.get(code).push(product);
            }

            const options = [];
            for (const [productCode, rows] of byCode) {
                // Base row: additionalService !== true, lowest productSequence when several.
                const baseRows = rows
                    .filter((p) => p.additionalService !== true)
                    .sort(
                        (a, b) =>
                            (parseInt(a.productSequence) || 999999) -
                            (parseInt(b.productSequence) || 999999)
                    );
                const baseRow = baseRows[0] || null;
                const label =
                    (baseRow && baseRow.productName) || productCode;
                options.push({ productCode, label, baseRow, products: rows });
            }
            return options;
        },

        // Trip summary passed to the pure cost logic. orderCount drives slab bracket selection
        // and the STOPS quantity; routeCode drives the flat per-route line; the per-leg road
        // distances drive SAMEDAY PICKUPMILES/DELIVERYMILES quantities (resolved on product
        // select via loadLegDistances).
        tripSummary() {
            return {
                orderCount: this.tripQuantities.orderCount,
                totalDistance: this.tripQuantities.totalDistance,
                routeCode: this.tripData?.routeCode,
                pickupLegDistance: this.pickupLegDistance,
                deliveryLegDistance: this.deliveryLegDistance,
            };
        },

        // Authoritative cost preview: the SAME pure buildCostNode used at approval, so the
        // displayed totals always equal what will be persisted. It excludes zero/blank lines and
        // sums tripTotal / fuelSurcharge over the kept lines. Recomputes reactively as the
        // operator edits quantities.
        costPreview() {
            return buildCostNode(this.costLines, this.tripSummary);
        },

        // Per-row preview for display. Each working line is run through the same computeLine the
        // approval node uses (amount/fuelSurcharge/total, slab forces qty 1), with a `_kept` flag
        // marking whether the line survives the zero/blank exclusion so excluded rows can be
        // visually de-emphasized. This keeps rows aligned 1:1 with the editable costLines inputs
        // (buildCostNode drops excluded lines, which would misalign the v-model indexes).
        previewLines() {
            const trip = this.tripSummary;
            return this.costLines.map((line) => {
                const computed = computeLine(line, trip);
                return {
                    ...computed,
                    _kept: Number(computed.quantity) > 0,
                };
            });
        },
    },

    methods: {
        display(val) {
            return (val === null || val === undefined || val === "") ? "—" : val;
        },

        // Format a numeric amount as currency for the preview. Non-numeric/blank shows a dash.
        money(val) {
            const n = Number(val);
            if (!Number.isFinite(n)) return "—";
            return n.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
            });
        },

        closeTripCostEditor() {
            this.$emit("closeTripCostEditor", false);
        },

        closeAlertBox(toggleAlertBox) {
            this.toggleAlertBox = toggleAlertBox;
        },

        // 1) Load the TripSheet identity (+ costStatus, vendor). Inline query omits
        //    createdAt/updatedAt to avoid AWSDateTime null issues.
        async loadTripSheet() {
            const graphqlQuery = /* GraphQL */ `
                query GetTripSheetForCost($TripSheetId: Int!) {
                    getTripSheet(TripSheetId: $TripSheetId) {
                        TripSheetId
                        routeCode
                        driverCode
                        vendor
                        vendorName
                        TripDate
                        SignOffDate
                        closedOn
                        costStatus
                    }
                }
            `;
            try {
                const resp = await API.graphql({
                    query: graphqlQuery,
                    variables: { TripSheetId: Number(this.tripSheetId) },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                this.tripData = resp?.data?.getTripSheet ?? null;
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = "Error loading trip";
            }
        },

        // 2) Load the TripWaypoint "*" summary record for default quantities (display-only).
        //    TripWaypoint.tripSheetID is a String; the summary row uses waypoint "*".
        async loadTripQuantities() {
            const graphqlQuery = /* GraphQL */ `
                query GetTripWaypointSummary($tripSheetID: String!, $waypoint: String!) {
                    getTripWaypoint(tripSheetID: $tripSheetID, waypoint: $waypoint) {
                        tripSheetID
                        waypoint
                        orderCount
                        totalDistance
                        totalTime
                    }
                }
            `;
            try {
                const resp = await API.graphql({
                    query: graphqlQuery,
                    variables: { tripSheetID: String(this.tripSheetId), waypoint: "*" },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                const summary = resp?.data?.getTripWaypoint;
                if (summary) {
                    this.tripQuantities = {
                        orderCount: summary.orderCount,
                        totalDistance: summary.totalDistance,
                        totalTime: summary.totalTime,
                    };
                }
            } catch (e) {
                console.log(e);
                // Non-fatal: the editor still renders without pre-populated quantities.
            }
        },

        // 3a) Load the Vendor record for fuel surcharge rates + vendorName (Requirement 2.6).
        async loadVendor() {
            const vendorCode = this.tripData?.vendor;
            if (!vendorCode) return;
            const graphqlQuery = /* GraphQL */ `
                query GetVendorForCost($vendor: String!) {
                    getVendor(vendor: $vendor) {
                        vendor
                        vendorName
                        fuelSurcharge
                    }
                }
            `;
            try {
                const resp = await API.graphql({
                    query: graphqlQuery,
                    variables: { vendor: vendorCode },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                const vendor = resp?.data?.getVendor;
                if (vendor) {
                    this.vendorName = vendor.vendorName || null;
                    this.fuelSurchargeList = this.parseFuelSurcharge(vendor.fuelSurcharge);
                }
            } catch (e) {
                console.log(e);
                // Non-fatal: lines still source prices; FS rate falls back to null.
            }
        },

        // fuelSurcharge is stored as AWSJSON (a list). Parse defensively.
        parseFuelSurcharge(raw) {
            if (!raw) return [];
            let parsed = raw;
            if (typeof raw === "string") {
                try {
                    parsed = JSON.parse(raw);
                } catch (e) {
                    return [];
                }
            }
            return Array.isArray(parsed) ? parsed : [];
        },

        // Look up the fuel surcharge rate for a product's fuelSurchargeCode from the Vendor list.
        fuelSurchargeRateFor(code) {
            if (!code) return null;
            const match = this.fuelSurchargeList.find((fs) => fs.fuelSurchargeCode === code);
            return match ? (match.fuelSurchargeRate ?? null) : null;
        },

        // 3b) Load TRIP-network products (prices) from the Product table (Requirement 1.1),
        //     matched by productCode + serviceCode. Uses the productByServiceNetwork index.
        //     The TRIP service network supersedes the previous vendor-network products
        //     (Requirements 1.1, 1.3, 1.4). The selection set includes tierPrice (for slab vs
        //     per-unit line math), serviceGroup, and productFamily (for product grouping);
        //     createdAt/updatedAt are omitted to avoid AWSDateTime null issues.
        async loadTripProducts() {
            const graphqlQuery = /* GraphQL */ `
                query TripProductsForCost($serviceNetwork: String!, $nextToken: String) {
                    productByServiceNetwork(serviceNetwork: $serviceNetwork, limit: 500, nextToken: $nextToken) {
                        items {
                            productCode
                            serviceCode
                            description
                            productName
                            serviceNetwork
                            vendor
                            price
                            fuelSurchargeCode
                            additionalService
                            serviceGroup
                            productFamily
                            productSequence
                            tierPrice {
                                start
                                end
                                price
                                slab
                                allowed
                            }
                        }
                        nextToken
                    }
                }
            `;
            const products = [];
            let nextToken = null;
            try {
                do {
                    const resp = await API.graphql({
                        query: graphqlQuery,
                        variables: { serviceNetwork: "TRIP", nextToken },
                        authMode: "AMAZON_COGNITO_USER_POOLS",
                    });
                    const payload = resp?.data?.productByServiceNetwork ?? {};
                    products.push(...(payload.items ?? []));
                    nextToken = payload?.nextToken ?? null;
                } while (nextToken);
                this.tripProducts = products;
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = "Error loading trip products";
            }
        },

        // Handle a payment-model selection from the Cost Lines dropdown (Requirement 7.1) and
        // build the editable working cost lines for the chosen product (Requirements 7.2–7.5).
        //
        // The selected product group is the base TC row (additionalService !== true) plus every
        // additionalService === true row sharing the same productCode. One working line is
        // rendered per service (Requirements 7.2, 7.3). Each line's quantity is seeded by the
        // pure serviceCode-matched rule `autoQuantityFor` (Requirement 7.4) using the trip summary
        // (orderCount, totalDistance) and the trip's routeCode. Each line carries its own
        // tierPrice/price/fuelSurcharge metadata so the approval-time line math (Task 6.3) can
        // compute slab vs per-unit amounts. Lines stay editable until approval (Requirement 7.5).
        async onSelectProduct(productCode) {
            this.selectedProductCode = productCode || "";

            if (!this.selectedProductCode) {
                this.costLines = [];
                return;
            }

            const option = this.tripProductOptions.find(
                (opt) => opt.productCode === this.selectedProductCode
            );
            if (!option) {
                this.costLines = [];
                return;
            }

            // The group's `products` are exactly the rows sharing this productCode: the base
            // row (additionalService !== true) plus every additionalService === true row. Render
            // one working line per service, base row first then additional services.
            const baseServices = option.products.filter((row) => row.additionalService !== true);
            const additionalServices = option.products.filter(
                (row) => row.additionalService === true
            );
            const services = [...baseServices, ...additionalServices];

            // SAMEDAY products carry PICKUPMILES / DELIVERYMILES, whose quantities come from the
            // per-leg road distances on the TripWaypoint sequence 1/2 records. Fetch those once
            // (via the tripWaypointByStop GSI) before seeding so autoQuantityFor can resolve them.
            const needsLegDistances = services.some((s) => {
                const code = (s.serviceCode || "").toUpperCase();
                return code === "PICKUPMILES" || code === "DELIVERYMILES";
            });
            if (needsLegDistances) {
                await this.loadLegDistances();
            }

            this.buildCostLinesFor(services);
        },

        // Build the editable working cost lines for the given services, seeding each quantity via
        // the pure serviceCode-matched rule using the full trip summary (which includes the SAMEDAY
        // leg distances). Each line carries its own tierPrice/price/fuelSurcharge metadata so the
        // approval-time line math can compute slab vs per-unit amounts. Lines stay editable until
        // approval.
        buildCostLinesFor(services) {
            const trip = this.tripSummary;
            this.costLines = services.map((service) => ({
                productCode: service.productCode,
                serviceCode: service.serviceCode,
                description: service.description || service.productName || null,
                quantity: autoQuantityFor(service, trip),
                price: service.price ?? null,
                tierPrice: service.tierPrice || null,
                fuelSurchargeCode: service.fuelSurchargeCode || null,
                fuelSurchargeRate: this.fuelSurchargeRateFor(service.fuelSurchargeCode),
            }));
        },

        // Resolve the SAMEDAY per-leg road distances from the TripWaypoint sequence 1 (depot->
        // pickup) and sequence 2 (pickup->delivery) records. Uses the tripWaypointByStop GSI
        // (PK tripSheetID, SK stop) sorted ascending with a small limit, then matches on the
        // `sequence` attribute. `fromPreviousDistance` is the Esri road distance (not Haversine).
        // Missing records leave the distance null so the quantity defaults to blank for operator
        // entry. Inline query omits createdAt/updatedAt (AWSDateTime-null pattern).
        async loadLegDistances() {
            this.loadingLegDistances = true;
            const graphqlQuery = /* GraphQL */ `
                query TripWaypointLegsForCost($tripSheetID: String!, $limit: Int) {
                    tripWaypointByStop(tripSheetID: $tripSheetID, sortDirection: ASC, limit: $limit) {
                        items {
                            tripSheetID
                            waypoint
                            stop
                            sequence
                            fromPreviousDistance
                        }
                    }
                }
            `;
            try {
                const resp = await API.graphql({
                    query: graphqlQuery,
                    variables: { tripSheetID: String(this.tripSheetId), limit: 5 },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                const items = resp?.data?.tripWaypointByStop?.items ?? [];
                const seq1 = items.find((w) => w.sequence === 1);
                const seq2 = items.find((w) => w.sequence === 2);
                this.pickupLegDistance = seq1 ? (seq1.fromPreviousDistance ?? null) : null;
                this.deliveryLegDistance = seq2 ? (seq2.fromPreviousDistance ?? null) : null;
            } catch (e) {
                console.log(e);
                // Non-fatal: leg distances stay null, PICKUPMILES/DELIVERYMILES default to blank.
                this.pickupLegDistance = null;
                this.deliveryLegDistance = null;
            } finally {
                this.loadingLegDistances = false;
            }
        },

        // Current date as "YYYY-MM-DD" for costApprovedOn (date-only string).
        todayDateOnly() {
            const now = new Date();
            const yyyy = now.getFullYear();
            const mm = String(now.getMonth() + 1).padStart(2, "0");
            const dd = String(now.getDate()).padStart(2, "0");
            return `${yyyy}-${mm}-${dd}`;
        },

        // Approve the trip cost: write cost (AWSJSON), costStatus "approved", costApprovedOn, and
        // vendorName in a single updateTripSheet mutation (Requirement 2.1). The record becomes
        // read-only immediately (Requirement 2.7). The inline mutation omits createdAt/updatedAt
        // to avoid AWSDateTime null issues (established codebase pattern).
        async approveTripCost() {
            if (this.approving || this.isApproved || !this.tripData) return;

            // The cost function is restricted to the BillingAdmin group (Requirement 3.5 /
            // Task 20). Defense-in-depth in case the control is reached without the gate.
            if (!this.canEditCost) {
                this.toggleAlertBox = true;
                this.alertMessage = "Only BillingAdmin users can approve trip cost";
                return;
            }

            // A vendor is required to approve. The tripInvoice Lambda is vendor-scoped and will
            // silently skip a trip approved with no vendor, leaving a locked, un-invoiceable
            // trip. Block approval here and tell the operator to assign a driver with a vendor.
            if (!this.hasVendor) {
                this.toggleAlertBox = true;
                this.alertMessage =
                    "This trip has no vendor. Assign a driver with a vendor before approving.";
                return;
            }

            // Reuse the exact cost node shown in the live preview (costPreview), so the approved
            // value is identical to what the operator reviewed. It is built with the pure
            // buildCostNode (slab/per-unit math, zero/blank exclusion, totals) from the same trip
            // summary (orderCount drives slab bracket selection).
            const costNode = this.costPreview;
            const resolvedVendorName =
                this.vendorName || this.tripData.vendorName || null;

            const input = {
                TripSheetId: Number(this.tripSheetId),
                costStatus: "approved",
                costApprovedOn: this.todayDateOnly(),
                vendorName: resolvedVendorName,
                cost: JSON.stringify(costNode), // AWSJSON — stringified per codebase convention
            };

            this.approving = true;
            try {
                await API.graphql({
                    query: /* GraphQL */ `
                        mutation ApproveTripCost($input: UpdateTripSheetInput!) {
                            updateTripSheet(input: $input) {
                                TripSheetId
                                costStatus
                                costApprovedOn
                                vendorName
                                cost
                            }
                        }
                    `,
                    variables: { input },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                this.toggleAlertBox = true;
                this.alertMessage = "Trip cost approved";
                // Reflect the locked state immediately.
                this.tripData = {
                    ...this.tripData,
                    costStatus: "approved",
                    costApprovedOn: input.costApprovedOn,
                    vendorName: resolvedVendorName,
                };
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = "Error approving trip cost";
            } finally {
                this.approving = false;
            }
        },

        // Enforce the costStatus transition rules (Requirement 3) for the "ignored"/"abandoned"
        // lifecycle:
        //   3.1 "approved" is irreversible — blocked here (guarded by isApproved) and no controls
        //       are rendered once approved.
        //   3.2 "ignored"/"abandoned" are reversible — allowed back to "open" or to each other.
        //   3.4 Only BillingAdmin (Admin) users may set "ignored"/"abandoned" (or reverse them).
        // Direct-to-"approved" transitions go through approveTripCost() (Requirement 3.3) so the
        // cost node is written atomically; this method rejects "approved" as a target.
        async setStatus(nextStatus) {
            if (this.settingStatus || this.approving || !this.tripData) return;

            // 3.1 — an approved trip is locked and cannot change status.
            if (this.isApproved) {
                this.toggleAlertBox = true;
                this.alertMessage = "Approved trips are locked and cannot change status";
                return;
            }

            // Only ops-status targets are handled here. "approved" must use Approve so the cost
            // node is written atomically (Requirement 3.3); "open" is the reversal target.
            const allowedTargets = ["open", "ignored", "abandoned"];
            if (!allowedTargets.includes(nextStatus)) {
                this.toggleAlertBox = true;
                this.alertMessage = "Use Approve to approve a trip cost";
                return;
            }

            // No-op guard.
            if (nextStatus === this.currentStatus) return;

            // 3.4 — setting "ignored"/"abandoned" (and reversing them back to "open") is
            // restricted to the BillingAdmin group.
            if (!this.canSetOpsStatus) {
                this.toggleAlertBox = true;
                this.alertMessage = "Only BillingAdmin users can set ignored/abandoned";
                return;
            }

            const input = {
                TripSheetId: Number(this.tripSheetId),
                costStatus: nextStatus,
            };

            this.settingStatus = true;
            try {
                await API.graphql({
                    query: /* GraphQL */ `
                        mutation SetTripCostStatus($input: UpdateTripSheetInput!) {
                            updateTripSheet(input: $input) {
                                TripSheetId
                                costStatus
                            }
                        }
                    `,
                    variables: { input },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                this.tripData = {
                    ...this.tripData,
                    costStatus: nextStatus,
                };
                this.toggleAlertBox = true;
                this.alertMessage = `Trip cost status set to "${nextStatus}"`;
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = "Error updating trip cost status";
            } finally {
                this.settingStatus = false;
            }
        },

        // Read the current user's Cognito groups to gate the "ignored"/"abandoned" controls
        // (Requirement 3.4). Mirrors the group-reading pattern used by the dashboards.
        async loadUserGroups() {
            try {
                const user = await Auth.currentAuthenticatedUser();
                const groups = user?.signInUserSession?.idToken?.payload?.["cognito:groups"];
                if (groups) {
                    groups.forEach((group) => {
                        if (group === "Admin") this.AdminUser = true;
                        if (group === "BillingAdmin") this.BillingAdmin = true;
                    });
                }
            } catch (e) {
                console.log(e);
                // Non-fatal: without confirmed groups the ops-status controls stay hidden.
            }
        },

        async loadEditor() {
            this.loading = true;
            try {
                // TripSheet + waypoint quantities can load in parallel.
                await Promise.all([this.loadTripSheet(), this.loadTripQuantities()]);
                // Vendor lookup depends on the loaded TripSheet.vendor; products are independent.
                // Cost lines are no longer auto-built here — they are built on product selection
                // via onSelectProduct (Requirement 7.2) using the pure autoQuantityFor rule.
                await Promise.all([this.loadVendor(), this.loadTripProducts()]);
            } finally {
                this.loading = false;
            }
        },
    },

    mounted() {
        this.loadUserGroups();
        this.loadEditor();
    },
};
</script>

<style scoped>
.main-container-shipmentinfo {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
}

.inner-container {
    background-color: #fff;
    border-radius: 25px;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    width: 70%;
    max-width: 900px;
    max-height: 85vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.confirm-container-main {
    padding: 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.close-shipment-info-container {
    display: flex;
    justify-content: flex-end;
}

.close-shipment-info {
    width: 35px;
    height: 35px;
    cursor: pointer;
}

.confirm-header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.package-details-header {
    margin: 0;
}

.trip-id-badge {
    background-color: #e4e4e4;
    padding: 5px 15px;
    border-radius: 999px;
    font-weight: bold;
}

.tce-status-badge {
    background-color: #d4d4d4;
    padding: 2.5px 12px;
    border-radius: 999px;
    font-size: 12px;
    text-transform: capitalize;
}

.inner-overflow-container {
    overflow-y: auto;
}

.tce-section {
    margin-bottom: 15px;
    border: 1px solid #e2e2e2;
    border-radius: 15px;
    overflow: hidden;
}

.section-header {
    background-color: #f3f3f3;
    padding: 10px 15px;
}

.section-header h2 {
    margin: 0;
    font-size: 16px;
}

.section-body {
    padding: 15px;
}

.tce-placeholder {
    color: #6a6a6a;
    margin: 0;
}

.tce-identity-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.tce-field {
    display: flex;
    flex-direction: column;
}

.tce-label {
    font-size: 12px;
    color: #6a6a6a;
}

.tce-value {
    font-weight: bold;
}

.tce-quantities {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e2e2e2;
}

.tce-subheading {
    display: block;
    font-size: 13px;
    color: #6a6a6a;
    margin-bottom: 10px;
}

.tce-product-select {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
    max-width: 360px;
}

.tce-product-select select {
    padding: 6px 8px;
    border: 1px solid #cfcfcf;
    border-radius: 6px;
    font-size: 14px;
    background-color: #fff;
}

.tce-table-wrapper {
    width: 100%;
    overflow-x: auto;
}

.tce-table {
    width: 100%;
    border-collapse: collapse;
}

.tce-table thead th {
    background-color: #32ccfe;
    color: #fff;
    padding: 8px;
    text-align: left;
    font-size: 13px;
}

.tce-table tbody td {
    background-color: #f3f3f3;
    padding: 8px;
    text-align: left;
    border-bottom: 5px solid #fff;
    font-size: 13px;
}

.tce-num {
    text-align: right;
}

.tce-qty-input {
    width: 80px;
    padding: 4px 6px;
    border: 1px solid #cfcfcf;
    border-radius: 6px;
    font-size: 13px;
    text-align: right;
}

.tce-qty-input:disabled {
    background-color: #e9e9e9;
    cursor: not-allowed;
}

.tce-row-excluded td {
    opacity: 0.5;
}

.tce-table tfoot td {
    padding: 8px;
    font-size: 13px;
    background-color: #f3f3f3;
    border-top: 2px solid #d4d4d4;
}

.tce-totals-label {
    font-weight: bold;
    color: #6a6a6a;
}

.tce-grand-total td {
    background-color: #e8f8ff;
    font-weight: bold;
    font-size: 14px;
}

.tce-preview-note {
    margin: 8px 0 0;
    font-size: 12px;
    color: #6a6a6a;
    font-style: italic;
}

.tce-status-row {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
}

.tce-status-current {
    text-transform: capitalize;
}

.tce-locked-note {
    color: #b45309;
    font-weight: bold;
}

.tce-gate-note {
    font-style: italic;
}

.tce-no-vendor-note {
    color: #b45309;
    font-weight: bold;
    padding: 10px 12px;
    margin-bottom: 10px;
    background: #fff7ed;
    border: 1px solid #fdba74;
    border-radius: 6px;
}

.tce-status-controls {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e2e2e2;
}

.tce-status-buttons {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 8px;
}

.tce-actions {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 15px;
}

.tce-btn {
    cursor: pointer;
    border: 1px solid #a0a0a0;
    background-color: #f3f3f3;
    padding: 8px 16px;
    border-radius: 15px;
    transition-duration: .5s;
}

.tce-btn:hover {
    filter: brightness(0.9);
}

.tce-btn-approve {
    background-color: #32ccfe;
    color: #fff;
    border-color: #32ccfe;
    font-weight: bold;
}

.tce-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

@media only screen and (max-width: 1300px) {
    .inner-container {
        width: 85%;
    }
}

@media only screen and (max-width: 600px) {
    .inner-container {
        width: 95%;
    }
    .tce-identity-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>
